import { deletePaginationParams, AFTER_PARAM, BEFORE_PARAM } from './usePaginationParams';
import { useQueryParam } from './useQueryParam';

export type PaginationParamPair = {
  beforeParam: string;
  afterParam: string;
};

export type UseFilterParamOptions<T> = {
  /** Array of valid values to validate against. If provided, only these values will be accepted from URL. */
  validValues?: readonly T[];
  /** Custom "before" pagination param name to delete on filter change. Defaults to 'before'. */
  beforeParam?: string;
  /** Custom "after" pagination param name to delete on filter change. Defaults to 'after'. */
  afterParam?: string;
  /** Multiple pagination param pairs to delete on filter change. Use when filter affects multiple paginated lists. */
  paginationParams?: PaginationParamPair[];
};

/**
 * Hook for managing filter query parameters in the URL.
 * Automatically resets pagination when filter value changes.
 *
 * @param paramName - The URL search parameter name
 * @param defaultValue - Default value when parameter is not present or invalid
 * @param options - Optional configuration for validation and pagination params
 * @returns Tuple of [currentValue, setValue]
 *
 * @example
 * // Simple usage
 * const [status, setStatus] = useFilterParam('status', 'active');
 *
 * @example
 * // With validation
 * const [filter, setFilter] = useFilterParam('type', 'all', {
 *   validValues: ['all', 'pending', 'completed'],
 * });
 *
 * @example
 * // With custom pagination params
 * const [category, setCategory] = useFilterParam('category', 'default', {
 *   beforeParam: 'items_before',
 *   afterParam: 'items_after',
 * });
 *
 * @example
 * // With multiple pagination param pairs (for filters affecting multiple paginated lists)
 * const [filter, setFilter] = useFilterParam('filter', 'all', {
 *   paginationParams: [
 *     { beforeParam: 'list1-before', afterParam: 'list1-after' },
 *     { beforeParam: 'list2-before', afterParam: 'list2-after' },
 *   ],
 * });
 */
export const useFilterParam = <T extends string | undefined>(
  paramName: string,
  defaultValue: T,
  options?: UseFilterParamOptions<T>,
): [T, (value: T | undefined) => void] => {
  const { validValues, beforeParam, afterParam, paginationParams } = options || {};

  return useQueryParam<T>(
    (searchParams) => {
      const paramValue = searchParams.get(paramName);
      if (validValues) {
        if (paramValue && validValues.includes(paramValue as T)) {
          return paramValue as T;
        }
        return defaultValue;
      }
      return (paramValue || defaultValue) as T;
    },
    (value, searchParams) => {
      if (value && value !== defaultValue && typeof value === 'string') {
        searchParams.set(paramName, value);
      } else {
        searchParams.delete(paramName);
      }

      if (paginationParams && paginationParams.length > 0) {
        paginationParams.forEach(({ beforeParam, afterParam }) => {
          deletePaginationParams(searchParams, beforeParam, afterParam);
        });
      } else {
        deletePaginationParams(
          searchParams,
          beforeParam || BEFORE_PARAM,
          afterParam || AFTER_PARAM,
        );
      }
    },
  );
};
